import {
  type PurchaseOrder,
  type PurchaseOrderParty,
  type PurchaseOrderTotals,
  formatCurrency,
  formatDate,
  formatQuantity,
  getVisibleLineItems,
  parseAmount,
} from '../_lib/purchase-order';

function PartyBlock({
  label,
  party,
}: {
  label: string;
  party: PurchaseOrderParty;
}) {
  const details = [party.address, party.contact, party.email]
    .map((value) => value.trim())
    .filter(Boolean);

  return (
    <div>
      <p className="text-[9px] font-bold tracking-[0.14em] text-neutral-500 uppercase">
        {label}
      </p>
      <p className="mt-1.5 text-[13px] leading-snug font-bold text-neutral-900">
        {party.name.trim() || '—'}
      </p>
      {/* Keyed by position, not content: a contact name that happens to repeat
          a line of the address would otherwise collide. */}
      {details.map((line, index) => (
        <p
          key={`${label}-${index}`}
          className="mt-0.5 text-[11px] leading-snug whitespace-pre-line text-neutral-500"
        >
          {line}
        </p>
      ))}
    </div>
  );
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-end gap-4">
      <span className="text-[11px] text-neutral-500">{label}</span>
      <span className="text-[11px] font-bold text-neutral-900 tabular-nums">
        {value || '—'}
      </span>
    </div>
  );
}

function TotalRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-6">
      <span className="text-[11px] text-neutral-500">{label}</span>
      <span className="text-[11px] font-bold text-neutral-900 tabular-nums">
        {value}
      </span>
    </div>
  );
}

/**
 * An on-screen rendering of the same document `generatePurchaseOrderPdf`
 * produces. It is deliberately locked to a light, paper-like palette rather
 * than theme tokens — it represents a printed page, so it should look the same
 * whether the visitor is browsing in light or dark mode.
 */
export function PurchaseOrderDocument({
  order,
  totals,
}: {
  order: PurchaseOrder;
  totals: PurchaseOrderTotals;
}) {
  const currency = order.currency || 'USD';
  const visibleLineItems = getVisibleLineItems(order);

  return (
    <div className="overflow-hidden rounded-2xl bg-white p-6 text-neutral-900 shadow-[0_20px_50px_-28px_rgba(22,22,42,0.45)] sm:p-8">
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="min-w-0">
          <p className="text-lg font-extrabold tracking-tight text-neutral-900">
            PURCHASE ORDER
          </p>
          <div className="mt-3">
            <p className="text-[13px] leading-snug font-bold">
              {order.buyer.name.trim() || '—'}
            </p>
            {[order.buyer.address, order.buyer.contact, order.buyer.email]
              .map((value) => value.trim())
              .filter(Boolean)
              .map((line) => (
                <p
                  key={line}
                  className="mt-0.5 text-[11px] leading-snug whitespace-pre-line text-neutral-500"
                >
                  {line}
                </p>
              ))}
          </div>
        </div>

        <div className="space-y-1">
          <MetaRow label="PO number" value={order.poNumber.trim()} />
          <MetaRow label="Order date" value={formatDate(order.orderDate)} />
          <MetaRow
            label={order.shipByLabel ?? 'Ship by'}
            value={formatDate(order.shipByDate)}
          />
          <MetaRow label="Currency" value={currency} />
        </div>
      </div>

      <div className="mt-5 grid gap-3 border-t border-neutral-200 pt-5 sm:grid-cols-2">
        <div className="rounded-lg bg-neutral-100 p-4">
          <PartyBlock label="Supplier" party={order.supplier} />
        </div>
        <div className="rounded-lg bg-neutral-100 p-4">
          <PartyBlock label="Ship to" party={order.shipTo} />
        </div>
      </div>

      <div className="mt-5 grid grid-cols-3 gap-4">
        {[
          ['Incoterm', order.incoterm.trim()],
          ['Payment terms', order.paymentTerms.trim()],
          ['Total quantity', formatQuantity(totals.totalQuantity)],
        ].map(([label, value]) => (
          <div key={label}>
            <p className="text-[9px] font-bold tracking-[0.14em] text-neutral-500 uppercase">
              {label}
            </p>
            <p className="mt-1 text-[11px] leading-snug font-bold">
              {value || '—'}
            </p>
          </div>
        ))}
      </div>

      {/* table-fixed with explicit widths: the preview column is narrower than
          a real page, and an auto layout lets a long description push the line
          total out of view — the one number a reader checks first. */}
      <div className="mt-5">
        <table className="w-full table-fixed border-collapse text-[10px]">
          <colgroup>
            <col className="w-[5%]" />
            <col className="w-[15%]" />
            <col className="w-[26%]" />
            <col className="w-[11%]" />
            <col className="w-[10%]" />
            <col className="w-[8%]" />
            <col className="w-[12%]" />
            <col className="w-[13%]" />
          </colgroup>
          <thead>
            <tr className="bg-neutral-900 text-white">
              <th className="px-1.5 py-2 text-left font-bold">#</th>
              <th className="px-1.5 py-2 text-left font-bold">SKU</th>
              <th className="px-1.5 py-2 text-left font-bold">Description</th>
              <th className="px-1.5 py-2 text-left font-bold">HS code</th>
              <th className="px-1.5 py-2 text-right font-bold">Qty</th>
              <th className="px-1.5 py-2 text-left font-bold">Unit</th>
              <th className="px-1.5 py-2 text-right font-bold">Price</th>
              <th className="px-1.5 py-2 text-right font-bold">Total</th>
            </tr>
          </thead>
          <tbody>
            {visibleLineItems.length === 0 ? (
              <tr>
                <td
                  colSpan={8}
                  className="border border-neutral-200 px-2 py-6 text-center text-neutral-400"
                >
                  Add a line item to see it here.
                </td>
              </tr>
            ) : (
              visibleLineItems.map(({ index, item }, row) => (
                <tr key={item.id}>
                  <td className="border border-neutral-200 px-1.5 py-2 text-center text-neutral-500">
                    {row + 1}
                  </td>
                  <td className="border border-neutral-200 px-1.5 py-2 break-words">
                    {item.sku.trim()}
                  </td>
                  <td className="border border-neutral-200 px-1.5 py-2 break-words">
                    {item.description.trim()}
                  </td>
                  <td className="border border-neutral-200 px-1.5 py-2 break-words">
                    {item.hsCode.trim()}
                  </td>
                  <td className="border border-neutral-200 px-1.5 py-2 text-right tabular-nums">
                    {formatQuantity(parseAmount(item.quantity))}
                  </td>
                  <td className="border border-neutral-200 px-1.5 py-2 break-words">
                    {item.unit.trim()}
                  </td>
                  <td className="border border-neutral-200 px-1.5 py-2 text-right tabular-nums">
                    {formatCurrency(parseAmount(item.unitPrice), currency)}
                  </td>
                  <td className="border border-neutral-200 px-1.5 py-2 text-right font-semibold tabular-nums">
                    {formatCurrency(totals.lineTotals[index] ?? 0, currency)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-5 flex justify-end">
        <div className="w-full max-w-[260px] space-y-1.5">
          <TotalRow
            label="Subtotal"
            value={formatCurrency(totals.subtotal, currency)}
          />
          {totals.discount > 0 ? (
            <TotalRow
              label="Discount"
              value={`-${formatCurrency(totals.discount, currency)}`}
            />
          ) : null}
          {totals.tax > 0 ? (
            <TotalRow
              label={`Tax (${totals.taxPercent}%)`}
              value={formatCurrency(totals.tax, currency)}
            />
          ) : null}
          {totals.shipping > 0 ? (
            <TotalRow
              label="Shipping / freight"
              value={formatCurrency(totals.shipping, currency)}
            />
          ) : null}

          <div className="mt-2 flex items-center justify-between gap-4 rounded-lg bg-neutral-900 px-4 py-3 text-white">
            <span className="text-[11px] font-bold">Total due</span>
            <span className="text-base font-extrabold tabular-nums">
              {formatCurrency(totals.total, currency)}
            </span>
          </div>
        </div>
      </div>

      {order.notes.trim() ? (
        <div className="mt-5 border-t border-neutral-200 pt-4">
          <p className="text-[9px] font-bold tracking-[0.14em] text-neutral-500 uppercase">
            Notes and terms
          </p>
          <p className="mt-1.5 text-[11px] leading-relaxed whitespace-pre-line text-neutral-500">
            {order.notes.trim()}
          </p>
        </div>
      ) : null}

      <div className="mt-8 grid grid-cols-2 gap-8">
        {['Authorised signature', 'Date'].map((label) => (
          <div key={label}>
            <div className="h-px bg-neutral-300" />
            <p className="mt-1.5 text-[9px] font-bold tracking-[0.14em] text-neutral-500 uppercase">
              {label}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
